import { AnimatePresence, motion } from "framer-motion";
import { MediaItem } from "./types/dailyTaskRwo";

type ViewerState =
  | { open: false }
  | { open: true; item: MediaItem; url: string };

const formatDate = (d: Date | string) =>
  new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg)$/i;

export const MediaViewer = ({
  state,
  onClose,
  onDownload,
}: {
  state: ViewerState;
  onClose: () => void;
  onDownload: () => void;
}) => {
  if (!state.open) return null;
  const { item, url } = state;

  const isVideo = (item: MediaItem) => {
    if (item.type === "video") return true;
    if (item.type === "image") return false;
    // type === "document": decide by extension
    return VIDEO_EXT.test(item.key);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex flex-col bg-black/90 backdrop-blur-sm"
        onClick={onClose}
      >
        {/* Toolbar */}
        <div
          className="flex items-center justify-between px-5 py-4 text-white shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div>
            <p className="font-semibold text-sm">{item.place}</p>
            <p className="text-xs text-gray-400 mt-0.5">{item.work}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onDownload}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-semibold transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                />
              </svg>
              Download
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Media */}
        <div
          className="flex-1 flex items-center justify-center p-4 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {!isVideo(item) ? (
            <motion.img
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={url}
              alt={item.work}
              className="max-h-full max-w-full rounded-xl object-contain shadow-2xl"
            />
          ) : (
            <motion.video
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              src={url}
              controls
              autoPlay
              className="max-h-full max-w-full rounded-xl shadow-2xl"
            />
          )}
        </div>

        {/* Meta footer */}
        <div className="px-5 py-3 text-center text-xs text-gray-500 shrink-0">
          {formatDate(item.created_at)}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
